import { memo, useState } from "react";
import { Tooltip as TooltipPrimitive } from "@base-ui/react/tooltip";
import isEqual from "lodash/isEqual";

import { BaseTableV3CellCopyTooltip } from "components/BaseTableV3/BaseTableV3CellCopyButton";
import { BaseTableV3Cell } from "./BaseTableV3Cell";
import { BODY_ROW_CLASSES } from "components/BaseTableV3/tableClasses";

export const BaseTableV3Body = ({ table }) => {
  const [copyTooltipHandle] = useState(() => TooltipPrimitive.createHandle());
  const columnPinning = table.getState().columnPinning;
  return (
    <>
      <tbody>
        {table.getRowModel().rows.map(row => (
          <tr key={row.id} className={BODY_ROW_CLASSES}>
            {row.getVisibleCells().map(cell => (
              <BaseTableV3Cell
                key={cell.id}
                cell={cell}
                isSelected={row.getIsSelected()}
                columnPinning={columnPinning}
                copyTooltipHandle={copyTooltipHandle}
              />
            ))}
          </tr>
        ))}
      </tbody>
      <BaseTableV3CellCopyTooltip handle={copyTooltipHandle} />
    </>
  );
};

export const MemoizedBaseTableV3Body = memo(BaseTableV3Body, (prev, next) => {
  // Get row models
  const prevRows = prev.table.getRowModel().rows;
  const nextRows = next.table.getRowModel().rows;

  // 1. Compare row count and IDs (data changes)
  if (prevRows.length !== nextRows.length) return false;
  for (let i = 0; i < prevRows.length; i++) {
    if (prevRows[i].id !== nextRows[i].id) return false;
  }

  // 2. Compare column visibility (affects which cells render)
  const prevVisibility = prev.table.getState().columnVisibility;
  const nextVisibility = next.table.getState().columnVisibility;
  if (!isEqual(prevVisibility, nextVisibility)) return false;

  // 3. Compare sorting (affects row order)
  const prevSorting = prev.table.getState().sorting;
  const nextSorting = next.table.getState().sorting;
  if (!isEqual(prevSorting, nextSorting)) return false;

  // 4. Compare pagination (affects which rows are visible)
  const prevPagination = prev.table.getState().pagination;
  const nextPagination = next.table.getState().pagination;
  if (!isEqual(prevPagination, nextPagination)) return false;

  // 5. Compare column order (affects cell order)
  const prevColumnOrder = prev.table.getState().columnOrder;
  const nextColumnOrder = next.table.getState().columnOrder;
  if (!isEqual(prevColumnOrder, nextColumnOrder)) return false;

  // 6. Compare column pinning (affects which columns are visible)
  const prevPinning = prev.table.getState().columnPinning;
  const nextPinning = next.table.getState().columnPinning;
  if (!isEqual(prevPinning, nextPinning)) return false;

  // IMPORTANT: Do NOT compare columnSizing or columnSizingInfo
  // These change during resize, but CSS vars handle the width updates

  return true; // Skip re-render - only sizing changed, CSS handles it
});
